import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { logger } from "@/lib/logger";

interface FloorplanTourManagerProps {
  northId: string;
  zpid?: string;
  floorplanData?: any;
  tourData?: any;
  floorplanUpdatedAt?: string;
  tourUpdatedAt?: string;
  onRefresh?: () => void;
  onUpdate?: () => void;
  isPublished?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
}

export const FloorplanTourManager = ({
  northId,
  zpid,
  floorplanData,
  tourData,
  floorplanUpdatedAt,
  tourUpdatedAt,
  onRefresh,
  onUpdate,
  isPublished = false,
  primaryColor,
  secondaryColor,
}: FloorplanTourManagerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOnReport, setShowOnReport] = useState(false);

  // Load visibility setting
  useEffect(() => {
    const loadVisibilitySetting = async () => {
      try {
        const { data, error } = await supabase
          .from('norths')
          .select('show_floorplan_tour_on_report, floorplan_data, tour_data')
          .eq('id', northId)
          .single();

        if (error) throw error;

        // If explicitly set in database, use that value
        if (data?.show_floorplan_tour_on_report !== null && data?.show_floorplan_tour_on_report !== undefined) {
          setShowOnReport(data.show_floorplan_tour_on_report);
        } else {
          // If not set, default to true if there's floorplan or tour data
          const hasData = (data?.floorplan_data && (Array.isArray(data.floorplan_data) ? data.floorplan_data.length > 0 : data.floorplan_data?.floorPlans?.length > 0)) ||
                         (data?.tour_data && (data.tour_data.tourUrl || data.tour_data.viewerUrl || data.tour_data.embedUrl));
          if (hasData) {
            setShowOnReport(true);
            // Update the database to reflect this default
            await supabase
              .from('norths')
              .update({ show_floorplan_tour_on_report: true })
              .eq('id', northId);
          }
        }
      } catch (error) {
        logger.error('Error loading visibility setting:', error);
      }
    };
    loadVisibilitySetting();
  }, [northId]);

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_floorplan_tour_on_report: checked })
        .eq('id', northId);

      if (error) throw error;
      setShowOnReport(checked);
      
      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }
      
      toast.success(checked ? "Shown on report" : "Hidden from report", {
        description: checked 
          ? "Floorplan & Tour will appear on the published report" 
          : "Floorplan & Tour are hidden from the published report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast.error("Failed to update visibility setting");
    }
  };

  const handleFetch = async () => {
    if (!zpid) {
      toast.error("Missing property ID", {
        description: "Property ID (ZPID) is required to fetch floorplan and tour data.",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('fetch-zillow-floorplan-tour', {
        body: { zpid, northId },
      });

      if (error) {
        logger.error('Error fetching floorplan and tour:', error);
        toast.error("Failed to fetch data", {
          description: "There was an error fetching the floorplan and 3D tour data.",
        });
        setIsLoading(false);
        return;
      }

      const { floorplanFetched, tourFetched } = data;
      
      if (floorplanFetched && tourFetched) {
        toast.success("Data fetched successfully", {
          description: "Both floorplan and 3D tour data have been updated.",
        });
      } else if (floorplanFetched) {
        toast.success("Floorplan fetched", {
          description: "Floorplan data updated. 3D tour not available for this property.",
        });
      } else if (tourFetched) {
        toast.success("3D tour fetched", {
          description: "3D tour data updated. Floorplan not available for this property.",
        });
      } else {
        toast.warning("No data available", {
          description: "Neither floorplan nor 3D tour data is available for this property.",
        });
      }

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      logger.error('Error:', error);
      toast.error("Error occurred", {
        description: "An unexpected error occurred while fetching the data.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle both normalized structure and raw array from API
  const floorPlans = Array.isArray(floorplanData) 
    ? floorplanData 
    : floorplanData?.floorPlans || [];
  const hasFloorplan = floorPlans.length > 0;
  const hasTour = tourData && (tourData.tourUrl || tourData.viewerUrl || tourData.embedUrl);

  return (
    <div className="space-y-4">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">Floorplan & 3D Tour</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Fetch and display floorplan images and 3D virtual tours
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: '#22c55e' } : undefined}
            />
            <Label htmlFor="show-on-report" className="text-sm font-medium cursor-pointer whitespace-nowrap">
              Show on Report
            </Label>
          </div>
          <Button
            onClick={handleFetch}
            disabled={isLoading || !zpid || isPublished}
            size="sm"
            className="w-full sm:w-auto"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 md:mr-2 animate-spin" />
                <span className="hidden md:inline">Fetching...</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">{hasFloorplan || hasTour ? 'Refresh' : 'Fetch Data'}</span>
                <span className="md:hidden">{hasFloorplan || hasTour ? 'Refresh' : 'Fetch'}</span>
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/20 rounded-lg">
          {/* Floorplan Status */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {hasFloorplan ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">Floorplan</span>
            </div>
            {hasFloorplan ? (
              <div>
                <p className="text-xs text-muted-foreground">
                  {floorPlans.length} plan{floorPlans.length !== 1 ? 's' : ''}
                </p>
                {floorplanUpdatedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Updated {format(new Date(floorplanUpdatedAt), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Not available</p>
            )}
          </div>

          {/* 3D Tour Status */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {hasTour ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">3D Tour</span>
            </div>
            {hasTour ? (
              <div>
                <p className="text-xs text-muted-foreground">
                  {tourData.provider || 'Available'}
                </p>
                {tourUpdatedAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Updated {format(new Date(tourUpdatedAt), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Not available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
